"use client";

import React from "react";
import Link from "next/link";
import TechnologyBrandLogo, {
  type TechnologyBrandLogoId,
} from "@/components/technology/TechnologyBrandLogo";

const TechnologyFoundationSection = () => {
  const categories: {
    label: string;
    items: { name: string; logoId: TechnologyBrandLogoId }[];
  }[] = [
    {
      label: "CLOUD PLATFORMS",
      items: [
        { name: "Amazon Web Services", logoId: "aws" },
        { name: "Microsoft Azure", logoId: "azure" },
        { name: "Google Cloud", logoId: "googlecloud" },
      ],
    },
    {
      label: "DATA WAREHOUSES",
      items: [
        { name: "Snowflake", logoId: "snowflake" },
        { name: "Amazon Redshift", logoId: "redshift" },
        { name: "Azure Synapse", logoId: "azure_synapse" },
        { name: "Google BigQuery", logoId: "googlebigquery" },
      ],
    },
    {
      label: "ETL & INTEGRATION",
      items: [
        { name: "Informatica", logoId: "informatica" },
        { name: "Talend", logoId: "talend" },
        { name: "Azure Data Factory", logoId: "azure_data_factory" },
        { name: "AWS Glue", logoId: "aws_glue" },
      ],
    },
    {
      label: "ANALYTICS & REPORTING",
      items: [
        { name: "Microsoft Power BI", logoId: "powerbi" },
        { name: "Tableau", logoId: "tableau" },
        { name: "Looker", logoId: "looker" },
        { name: "Qlik", logoId: "qlik" },
      ],
    },
    {
      label: "DATA TRANSFORMATION",
      items: [
        { name: "dbt", logoId: "dbt" },
        { name: "Apache Airflow", logoId: "airflow" },
      ],
    },
    {
      label: "DATA GOVERNANCE",
      items: [
        { name: "Erwin", logoId: "erwin" },
        { name: "Collibra", logoId: "collibra" },
      ],
    },
  ];

  return (
    <section className="bg-white py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] text-left md:text-center mb-3 sm:mb-4">
          ENTERPRISE-GRADE TECHNOLOGY
        </p>

        <h2 className="font-serif text-navy text-[24px] sm:text-[30px] md:text-[38px] lg:text-[42px] leading-[1.2] font-bold text-left md:text-center mb-3 sm:mb-4">
          We Don&apos;t Build Proprietary Systems. We Implement Proven Platforms.
        </h2>

        <p className="text-charcoal text-[15px] sm:text-base md:text-lg leading-[1.6] text-left md:text-center max-w-[750px] md:mx-auto mb-8 sm:mb-12">
          Your EDW is built on the same technology stack trusted by Fortune 500 companies–reducing risk
          and ensuring scalability.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
          {categories.map((category, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 hover:border-gold transition-all duration-300"
            >
              <h3 className="text-navy text-xs font-bold uppercase tracking-wider mb-4 text-left md:text-center">
                {category.label}
              </h3>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {category.items.map((item) => (
                  <div
                    key={item.name}
                    className="aspect-square w-full min-w-0 bg-white rounded-lg sm:rounded-xl border border-gray-200 p-2.5 sm:p-3 flex flex-col items-center justify-center gap-2 sm:gap-2.5 text-center hover:border-gold/60 hover:shadow-sm transition-all duration-300"
                  >
                    <TechnologyBrandLogo
                      logoId={item.logoId}
                      className="h-9 w-9 sm:h-10 sm:w-10 text-navy opacity-90 shrink-0"
                    />
                    <span className="text-navy font-semibold text-[11px] sm:text-xs leading-tight min-w-0 break-words px-0.5">
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-navy text-[14px] sm:text-base leading-[1.6] text-left md:text-center max-w-[650px] md:mx-auto mb-6 sm:mb-8">
          This isn&apos;t experimental technology. These are battle-tested platforms with enterprise
          support, global infrastructure, and proven ROI.
        </p>

        <div className="flex justify-start md:justify-center">
          <Link href="/contact" className="inline-block text-center w-full sm:w-auto min-h-[48px] bg-navy text-white px-6 sm:px-8 py-4 rounded-lg font-semibold text-[14px] sm:text-base hover:bg-navy/90 transition-all active:scale-[0.98]">
            View Our Technology Stack Details →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TechnologyFoundationSection;
